/*
 * ONX header, mobile menu behaviour (snippets/onx-header-mobile.liquid). Vanilla, deferred, no globals, every lookup is
 * null guarded: on a page without the menu (or on desktop widths) it does nothing and throws nothing.
 *
 * Progressive enhancement: the hamburger is rendered as a plain link to the shop page. This script turns it into the
 * opener of a native <dialog> (showModal gives the top layer, an inert page behind, Escape and keyboard containment).
 * Without JS, or in a browser without dialog.showModal, the link simply works as a link, so the menu button is never dead.
 *
 * Does not use the theme drawer classes (js-drawer-open-*, data-drawer-*) and never touches theme.js objects.
 *
 * Menu model: a drill down (root screen, level 2 screen, level 3 screen), as drawn in Figma. One screen is visible at a time.
 */
(function () {
  'use strict';

  var doc = document;
  if (!doc || !doc.querySelectorAll || !doc.documentElement) return;

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  function escId(id) {
    return (window.CSS && window.CSS.escape) ? window.CSS.escape(id) : String(id).replace(/[^\w-]/g, '\\$&');
  }

  function visible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length)));
  }

  function init(dlg) {
    if (!dlg || dlg.getAttribute('data-onx-hm-ready') === '1') return;
    if (typeof dlg.showModal !== 'function' || typeof dlg.close !== 'function') return;
    dlg.setAttribute('data-onx-hm-ready', '1');

    var id = dlg.id;
    if (!id) return;
    var header = dlg.closest ? dlg.closest('[data-onx-header]') : null;
    var openers = [].slice.call(doc.querySelectorAll('[data-onx-mobile-open][aria-controls="' + escId(id) + '"]'));
    var searchOpeners = header ? [].slice.call(header.querySelectorAll('[data-onx-hm-search-wrap] a, [data-onx-hm-search-wrap] button')) : [];
    var screens = [].slice.call(dlg.querySelectorAll('[data-onx-hm-screen]'));
    var input = dlg.querySelector('[data-onx-hm-input]');
    var mq = window.matchMedia ? window.matchMedia('(min-width: 1200px)') : null;
    var stack = [];
    var lastOpener = null;
    var lock = null;

    /* ---------- scroll lock (no layout shift, scroll position restored) ---------- */
    function lockScroll() {
      if (lock) return;
      var de = doc.documentElement;
      var b = doc.body;
      if (!b) return;
      var y = window.pageYOffset || de.scrollTop || 0;
      var sbw = Math.max(0, window.innerWidth - de.clientWidth);
      lock = { y: y, position: b.style.position, top: b.style.top, left: b.style.left, right: b.style.right, width: b.style.width, pr: b.style.paddingRight };
      if (sbw > 0) {
        var cur = parseFloat(window.getComputedStyle(b).paddingRight) || 0;
        b.style.paddingRight = (cur + sbw) + 'px';
      }
      b.style.position = 'fixed';
      b.style.top = (-y) + 'px';
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = 'auto';
    }

    function unlockScroll() {
      if (!lock) return;
      var de = doc.documentElement;
      var b = doc.body;
      var s = lock;
      lock = null;
      if (!b) return;
      b.style.position = s.position;
      b.style.top = s.top;
      b.style.left = s.left;
      b.style.right = s.right;
      b.style.width = s.width;
      b.style.paddingRight = s.pr;
      var prev = de.style.scrollBehavior;
      de.style.scrollBehavior = 'auto';
      window.scrollTo(0, s.y);
      de.style.scrollBehavior = prev;
    }

    /* ---------- screens (drill down) ---------- */
    function findScreen(name) {
      for (var i = 0; i < screens.length; i++) {
        if (screens[i].getAttribute('data-onx-hm-screen') === name) return screens[i];
      }
      return null;
    }

    function current() {
      for (var i = 0; i < screens.length; i++) {
        if (!screens[i].hidden) return screens[i];
      }
      return null;
    }

    function show(target, dir) {
      if (!target) return;
      screens.forEach(function (s) {
        s.hidden = s !== target;
        s.classList.remove('is-fwd', 'is-back');
      });
      var body = target.querySelector('.onx-hm__body');
      if (body && dir === 'fwd') body.scrollTop = 0;
      if (dir) {
        // Reflow so the animation restarts, then clean the class up when it ends.
        void target.offsetWidth;
        target.classList.add(dir === 'fwd' ? 'is-fwd' : 'is-back');
        var clear = function () { target.classList.remove('is-fwd', 'is-back'); };
        target.addEventListener('animationend', clear, { once: true });
        window.setTimeout(clear, 400);
      }
    }

    function go(trigger) {
      var target = findScreen(trigger.getAttribute('data-onx-hm-go'));
      var cur = current();
      if (!target || !cur) return;
      stack.push({ id: cur.getAttribute('data-onx-hm-screen'), trigger: trigger });
      show(target, 'fwd');
      var back = target.querySelector('[data-onx-hm-back]');
      if (back) back.focus({ preventScroll: true });
    }

    function back() {
      var prev = stack.pop();
      if (!prev) return;
      show(findScreen(prev.id), 'back');
      if (prev.trigger && prev.trigger.focus) prev.trigger.focus({ preventScroll: true });
    }

    var onReset = null;
    function reset() {
      stack = [];
      show(findScreen('root'), null);
      if (input) input.value = '';
      if (onReset) onReset();
    }

    /* ---------- open and close ---------- */
    function setExpanded(on) {
      openers.forEach(function (o) { o.setAttribute('aria-expanded', on ? 'true' : 'false'); });
      if (header) {
        if (on) header.setAttribute('data-onx-menu-open', '');
        else header.removeAttribute('data-onx-menu-open');
      }
    }

    function open(opener, focusSearch) {
      if (dlg.open) return true;
      if (mq && mq.matches) return false;
      lastOpener = opener || doc.activeElement;
      reset();
      lockScroll();
      try {
        dlg.showModal();
      } catch (e) {
        unlockScroll();
        return false;
      }
      setExpanded(true);
      if (focusSearch && input) input.focus();
      return true;
    }

    function onClosed() {
      setExpanded(false);
      unlockScroll();
      reset();
      var o = lastOpener;
      lastOpener = null;
      if (o && o.focus && doc.contains(o)) o.focus({ preventScroll: true });
    }

    dlg.addEventListener('close', onClosed);

    function bindOpener(el, focusSearch) {
      el.addEventListener('click', function (ev) {
        if (ev.defaultPrevented || ev.button > 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
        if (mq && mq.matches) return; // desktop row is in charge: let the link work
        ev.preventDefault();
        if (!open(el, focusSearch)) {
          var href = el.getAttribute('href');
          if (href) window.location.href = href;
        }
      });
    }

    openers.forEach(function (el) {
      if (el.tagName === 'A') {
        el.setAttribute('role', 'button');
        el.addEventListener('keydown', function (ev) {
          if (ev.key === ' ' || ev.key === 'Spacebar') { ev.preventDefault(); el.click(); }
        });
      }
      bindOpener(el, false);
    });
    searchOpeners.forEach(function (el) { bindOpener(el, true); });

    /* ---------- inside the dialog ---------- */
    dlg.addEventListener('click', function (ev) {
      var t = ev.target && ev.target.closest ? ev.target.closest('[data-onx-hm-go], [data-onx-hm-back], [data-onx-hm-close], a[href]') : null;
      if (!t || !dlg.contains(t)) return;
      if (t.hasAttribute('data-onx-hm-go')) { go(t); return; }
      if (t.hasAttribute('data-onx-hm-back')) { back(); return; }
      if (t.hasAttribute('data-onx-hm-close')) { dlg.close(); return; }
      var href = t.getAttribute('href') || '';
      if (href.charAt(0) === '#') dlg.close(); // same page anchor: leave the menu so the page can scroll
    });

    // Keep Tab inside the visible screen (wrap at both ends).
    dlg.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Tab') return;
      var cur = current();
      if (!cur) return;
      var items = [].slice.call(cur.querySelectorAll(FOCUSABLE)).filter(function (el) { return visible(el) && el.getAttribute('tabindex') !== '-1'; });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = doc.activeElement;
      if (ev.shiftKey && (active === first || active === dlg)) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && active === last) { ev.preventDefault(); first.focus(); }
    });


    /* ---------- predictive search (theme endpoint, own markup) ---------- */
    var suggestUrl = dlg.getAttribute('data-onx-hm-suggest');
    var results = dlg.querySelector('[data-onx-hm-results]');
    var list = dlg.querySelector('[data-onx-hm-list]');
    var status = dlg.querySelector('[data-onx-hm-status]');
    var form = input && input.form;
    var rootScreen = findScreen('root');
    var suggestOpen = false;
    var escAt = 0;

    onReset = function () { closeResults(); };

    function setStatus(text) { if (status) status.textContent = text || ''; }

    function closeResults() {
      suggestOpen = false;
      if (results) results.hidden = true;
      if (list) list.textContent = '';
      if (rootScreen) rootScreen.classList.remove('has-results');
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
      setStatus('');
    }

    if (suggestUrl && input && results && list && form && window.fetch && window.DOMParser) {
      var timer = null;
      var ctrl = null;
      var seq = 0;
      var options = [];
      var active = -1;
      var ORDER = ['queries', 'products', 'collections', 'pages', 'articles'];

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', list.id);
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-haspopup', 'listbox');

      var el = function (tag, cls, text) {
        var n = doc.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
      };

      // Copy the highlighted text of a suggestion (only <mark> and plain text survive).
      var copyMarked = function (src, dst) {
        for (var i = 0; i < src.childNodes.length; i++) {
          var c = src.childNodes[i];
          if (c.nodeType === 3) dst.appendChild(doc.createTextNode(c.textContent));
          else if (c.nodeType === 1) {
            if (c.tagName === 'MARK') dst.appendChild(el('mark', '', c.textContent));
            else dst.appendChild(doc.createTextNode(c.textContent));
          }
        }
      };

      var parse = function (html) {
        var d = new DOMParser().parseFromString(html, 'text/html');
        var groups = {};
        var blocks = d.querySelectorAll('.predictive-search-results .results');
        for (var i = 0; i < blocks.length; i++) {
          var m = /results--(\w+)/.exec(blocks[i].className);
          if (!m) continue;
          var h = blocks[i].querySelector('h3');
          var items = [];
          var anchors = blocks[i].querySelectorAll('li a');
          for (var j = 0; j < anchors.length; j++) {
            var a = anchors[j];
            var img = a.querySelector('img');
            var priceEl = a.querySelector('.grid-product__price');
            var textEl = a.querySelector('.results-products__info > span') || a.querySelector('span');
            items.push({ href: a.getAttribute('href') || '', text: textEl, img: img, price: priceEl ? priceEl.textContent.trim() : '' });
          }
          groups[m[1]] = { title: h ? h.textContent.trim() : m[1], items: items };
        }
        return groups;
      };

      var allHref = function (term) {
        var base = form.getAttribute('action') || '/search';
        return base + (base.indexOf('?') > -1 ? '&' : '?') + 'q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
      };

      var render = function (groups, term) {
        var frag = doc.createDocumentFragment();
        var uid = 0;
        var count = 0;
        options = [];
        ORDER.forEach(function (key) {
          var g = groups[key];
          if (!g || !g.items.length) return;
          var gid = id + '-g-' + key;
          var wrap = el('div', 'onx-hm__rg');
          wrap.setAttribute('role', 'group');
          wrap.setAttribute('aria-labelledby', gid);
          var head = el('div', 'onx-hm__rh', g.title);
          head.id = gid;
          wrap.appendChild(head);
          g.items.forEach(function (it) {
            var a = el('a', 'onx-hm__opt' + (key === 'products' ? ' onx-hm__opt--product' : ''));
            a.href = it.href;
            a.id = id + '-o-' + (uid++);
            a.setAttribute('role', 'option');
            a.setAttribute('tabindex', '-1');
            a.setAttribute('aria-selected', 'false');
            if (key === 'products') {
              if (it.img) {
                var thumb = el('span', 'onx-hm__thumb');
                var im = doc.createElement('img');
                im.src = it.img.getAttribute('src') || '';
                if (it.img.getAttribute('srcset')) im.setAttribute('srcset', it.img.getAttribute('srcset'));
                im.sizes = '56px';
                im.width = 56;
                im.height = 56;
                im.alt = '';
                im.loading = 'lazy';
                im.decoding = 'async';
                thumb.appendChild(im);
                a.appendChild(thumb);
              }
              var t = el('span', 'onx-hm__opt-text');
              t.appendChild(el('span', '', it.text ? it.text.textContent.trim() : ''));
              if (it.price) t.appendChild(el('span', 'onx-hm__price', it.price));
              a.appendChild(t);
            } else {
              var sp = el('span');
              if (key === 'queries' && it.text) copyMarked(it.text, sp); else sp.textContent = it.text ? it.text.textContent.trim() : '';
              a.appendChild(sp);
            }
            wrap.appendChild(a);
            options.push(a);
            count++;
          });
          frag.appendChild(wrap);
        });
        if (!count) {
          frag.appendChild(el('p', 'onx-hm__none', (results.getAttribute('data-t-none') || 'No results for') + ' “' + term + '”'));
        }
        var all = el('a', 'onx-hm__opt onx-hm__opt--all');
        all.href = allHref(term);
        all.id = id + '-o-' + (uid++);
        all.setAttribute('role', 'option');
        all.setAttribute('tabindex', '-1');
        all.setAttribute('aria-selected', 'false');
        all.appendChild(el('span', '', (results.getAttribute('data-t-all') || 'View all results for') + ' “' + term + '”'));
        frag.appendChild(all);
        options.push(all);

        list.textContent = '';
        list.appendChild(frag);
        active = -1;
        input.removeAttribute('aria-activedescendant');
        results.hidden = false;
        results.scrollTop = 0;
        if (rootScreen) rootScreen.classList.add('has-results');
        input.setAttribute('aria-expanded', 'true');
        suggestOpen = true;
        setStatus(count ? count + ' ' + (results.getAttribute('data-t-count') || 'suggestions available') : (results.getAttribute('data-t-none') || 'No results for') + ' ' + term);
      };

      var fetchTerm = function (term) {
        if (ctrl && ctrl.abort) ctrl.abort();
        ctrl = window.AbortController ? new AbortController() : null;
        var mine = ++seq;
        var sep = suggestUrl.indexOf('?') > -1 ? '&' : '?';
        var url = suggestUrl + sep + 'q=' + encodeURIComponent(term) + '&resources%5Blimit%5D=4&resources%5Blimit_scope%5D=each&section_id=search-results';
        fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
          .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
          .then(function (html) { if (mine === seq) render(parse(html), term); })
          .catch(function (e) { if (e && e.name === 'AbortError') return; if (mine === seq) closeResults(); });
      };

      var setActive = function (n) {
        if (active > -1 && options[active]) {
          options[active].classList.remove('is-active');
          options[active].setAttribute('aria-selected', 'false');
        }
        active = n;
        if (active > -1 && options[active]) {
          options[active].classList.add('is-active');
          options[active].setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', options[active].id);
          if (options[active].scrollIntoView) options[active].scrollIntoView({ block: 'nearest' });
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      };

      input.addEventListener('input', function () {
        window.clearTimeout(timer);
        var term = input.value.trim();
        if (!term) { seq++; if (ctrl && ctrl.abort) ctrl.abort(); closeResults(); return; }
        timer = window.setTimeout(function () { fetchTerm(term); }, 250);
      });

      input.addEventListener('keydown', function (ev) {
        if (!suggestOpen || !options.length) return;
        if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive(active + 1 >= options.length ? 0 : active + 1); }
        else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive(active - 1 < 0 ? options.length - 1 : active - 1); }
        else if (ev.key === 'Enter' && active > -1) { ev.preventDefault(); options[active].click(); }
      });

      // Escape: first close the results (keep the typed text), the next Escape closes the menu.
      dlg.addEventListener('keydown', function (ev) {
        if (ev.key !== 'Escape') return;
        if (suggestOpen) {
          ev.preventDefault();
          ev.stopPropagation();
          escAt = Date.now();
          closeResults();
          if (input) input.focus({ preventScroll: true });
        } else if (ev.target === input && input.value) {
          // Browsers clear a search field on Escape and swallow the key: close the menu instead, as everywhere else.
          ev.preventDefault();
          dlg.close();
        }
      }, true);
      dlg.addEventListener('cancel', function (ev) {
        if (suggestOpen || Date.now() - escAt < 100) ev.preventDefault();
      });
    }

    // Leaving for desktop widths, or coming back from the back/forward cache with the menu still open: close it.
    function onMq() { if (mq && mq.matches && dlg.open) dlg.close(); }
    if (mq) {
      if (mq.addEventListener) mq.addEventListener('change', onMq);
      else if (mq.addListener) mq.addListener(onMq);
    }
    window.addEventListener('pageshow', function (ev) { if (ev.persisted && dlg.open) dlg.close(); });
  }

  function boot() {
    var list = doc.querySelectorAll('dialog[data-onx-hm]');
    for (var i = 0; i < list.length; i++) init(list[i]);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
